import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { handleUnauthorizedAccess } from "@/lib/access-control";

export const ProtectedRoute = ({ children, requireApproval = true }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) return;

    // Not authenticated - redirect to login, remembering where we were
    if (!isAuthenticated) {
      const returnTo = `${location.pathname}${location.search}`;
      handleUnauthorizedAccess(navigate, { returnTo });
      return;
    }

    // Check if user is approved (if required)
    if (requireApproval && user && !user.is_approved) {
      navigate("/aguardar-aprovacao", { replace: true });
    }
  }, [loading, isAuthenticated, user, requireApproval, navigate, location.pathname, location.search]);

  // Loading state - AuthContext is still checking
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Not authenticated - will redirect via useEffect
  if (!isAuthenticated) {
    return null;
  }

  // Check approval requirement
  if (requireApproval && user && !user.is_approved) {
    return null;
  }

  // Authenticated - render children with user context
  // Profile completion is now handled globally by App.js
  if (children) {
    return typeof children === 'function' ? children({ user }) : 
      <div data-user={JSON.stringify(user)}>{children}</div>;
  }

  return null;
};
